/**
 * CaseAgentPolicySection — per-case agent enablement/review override (RA-TOGGLE-UI,
 * BR-AGENT-POLICY-CASE-SCOPE), plus the RA-PAUSE-CONTROL manual-pause trigger.
 *
 * Repair-owned. Deliberately NOT extracted from LeadAgentPolicySection
 * (apps/landlord/src/pages/leasing/lead-detail/OverviewTab.tsx, which stays local/unexported
 * there) — duplicating that component's state machine here for the case scope is intentional
 * per this slice's design, rather than generalizing a shared component across two different
 * scope kinds in one pass. Mirrors its loading/error/retry state machine and its
 * `lead-agent-policy-*` test-id convention, under a `case-agent-policy-*` prefix.
 *
 * Endpoint: GET/PATCH /landlord/api/cases/:id/agent-policy (BR-AGENT-POLICY-CASE-SCOPE).
 * Cascade order for a case-scoped resource with no Lead/Listing (e.g. repair):
 * case -> landlord -> system (apps/api/src/services/agent/agent-enablement.service.ts).
 *
 * Layout: rendered inline (no card of its own) inside RepairDetailPage's summary card, per
 * the design mockup — two compact switches with a one-line status caption each, a `?` help
 * tip and a small "Pause agent" button.
 *
 * Iteration 12 (UX restructure): the prior two <select> + "Save policy" submit flow is
 * replaced by two immediate-toggle Switch controls — each mutates independently via PATCH
 * as soon as it's flipped, with an inline "Use default" link to reset that one field back
 * to 'inherit'. No submit step remains.
 *
 * RA-PAUSE-CONTROL: this section's header also carries the "Pause agent" trigger (POST
 * .../agent-suspension/pause) — hidden while the case is already paused (`agentState ===
 * 'paused'`). This is a repair-only control (this component is NOT shared with
 * leasing-support), so adding it here does not touch the shared substrate.
 *
 * Types are derived from the boundary contract in `@resida/api-types/agent` — the same Zod
 * schemas apps/api registers on this route.
 */

import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Switch } from '../ui/switch';
import { HelpTooltip } from '../ui/HelpTooltip';
import { pauseAgent } from '../../api/agent-escalations.api';
import type {
  AgentEnablementDecision as AgentEnablement,
  AgentReviewDecision as AgentReview,
  PolicySource,
  AgentPolicyResponse as AgentPolicyState,
} from '@resida/api-types/agent';

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function getCaseAgentPolicy(caseId: string, signal?: AbortSignal): Promise<AgentPolicyState> {
  const response = await fetch(
    `/landlord/api/cases/${encodeURIComponent(caseId)}/agent-policy`,
    { method: 'GET', credentials: 'include', signal }
  );
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const err = await response.json() as { message?: string };
      if (err.message) message = err.message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  return response.json() as Promise<AgentPolicyState>;
}

async function patchCaseAgentPolicy(
  caseId: string,
  body: { enablement?: AgentEnablement; review?: AgentReview }
): Promise<AgentPolicyState> {
  const response = await fetch(
    `/landlord/api/cases/${encodeURIComponent(caseId)}/agent-policy`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const err = await response.json() as { message?: string };
      if (err.message) message = err.message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  return response.json() as Promise<AgentPolicyState>;
}

function formatCasePolicySource(source: PolicySource): string {
  if (source === 'lead') return 'lead policy';
  if (source === 'listing') return 'listing policy';
  if (source === 'landlord') return 'landlord default';
  return 'system default';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CaseAgentPolicySectionProps {
  caseId: string;
  /** RA-PAUSE-CONTROL: current agentState (RepairDetailPage passes repair.agentState)
   *  — hides the pause trigger while the case is already paused. Only the literal
   *  'paused' value hides it; every other state (including 'escalation_pending')
   *  still shows it, per RA-PAUSE-CONTROL's "hidden while suspended" scope, since
   *  an open escalation with no suspension is not "already suspended". */
  agentState?: string;
  /** Called after a successful manual pause — parent reloads repair/timeline/
   *  escalation section, mirroring RepairComposer's onSent. */
  onPaused?: () => void;
  /** Called after every successful enablement/review PATCH (toggle or reset-to-inherit) —
   *  mirrors CaseEscalationSection's own `onChanged` prop. This component's own local state
   *  (enablement, review, the resolved values, and their sources) is updated regardless, but
   *  the parent page derives its own `agentState` (and the Pause button's visibility) from
   *  that same resolved enablement, so without this callback the page's header badge and
   *  Pause button go stale until a full reload — only `handlePause` used to notify the
   *  parent. */
  onChanged?: () => void;
}

/** Exported for direct component testing, mirroring LeadAgentPolicySection's contract. */
export function CaseAgentPolicySection({ caseId, agentState, onPaused, onChanged }: CaseAgentPolicySectionProps) {
  const [enablement, setEnablement] = useState<AgentEnablement>('inherit');
  const [review, setReview] = useState<AgentReview>('inherit');
  const [resolvedEnablement, setResolvedEnablement] = useState<'enabled' | 'disabled'>('disabled');
  const [resolvedReview, setResolvedReview] = useState<'required' | 'not_required'>('required');
  const [enablementSource, setEnablementSource] = useState<PolicySource>('system');
  const [reviewSource, setReviewSource] = useState<PolicySource>('system');
  const [savingEnablement, setSavingEnablement] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  // True while the initial GET is in flight — switches are disabled during this period.
  const [loadingPolicy, setLoadingPolicy] = useState(true);
  // True if the most recent GET load failed — prevents toggling against wrong defaults.
  const [loadFailed, setLoadFailed] = useState(false);

  const [pausing, setPausing] = useState(false);
  const [pauseError, setPauseError] = useState<string | null>(null);

  const loadPolicy = useCallback(async (signal?: AbortSignal) => {
    setLoadingPolicy(true);
    setLoadFailed(false);
    setError(null);
    try {
      const stored = await getCaseAgentPolicy(caseId, signal);
      setEnablement(stored.enablement);
      setReview(stored.review);
      setResolvedEnablement(stored.resolvedEnablement);
      setResolvedReview(stored.resolvedReview);
      setEnablementSource(stored.enablementSource);
      setReviewSource(stored.reviewSource);
    } catch (err) {
      // A navigation away from this page while the GET is still in flight aborts the fetch —
      // that's expected, benign, and not this component's failure to report. Only a genuine
      // fetch failure (network error, non-2xx) gets logged/surfaced.
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      console.error('Failed to load case agent policy:', err);
      setError('Could not load agent policy settings. Refresh the page to try again.');
      setLoadFailed(true);
    } finally {
      if (!signal?.aborted) {
        setLoadingPolicy(false);
      }
    }
  }, [caseId]);

  useEffect(() => {
    const controller = new AbortController();
    void loadPolicy(controller.signal);
    return () => controller.abort();
  }, [loadPolicy]);

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const handleEnablementToggle = async (nextEnabled: boolean) => {
    setSavingEnablement(true);
    setError(null);
    try {
      const result = await patchCaseAgentPolicy(caseId, { enablement: nextEnabled ? 'enabled' : 'disabled' });
      setEnablement(result.enablement);
      setResolvedEnablement(result.resolvedEnablement);
      setEnablementSource(result.enablementSource);
      showSuccess('Agent enablement updated.');
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update agent enablement');
    } finally {
      setSavingEnablement(false);
    }
  };

  const handleResetEnablementToInherit = async () => {
    setSavingEnablement(true);
    setError(null);
    try {
      const result = await patchCaseAgentPolicy(caseId, { enablement: 'inherit' });
      setEnablement(result.enablement);
      setResolvedEnablement(result.resolvedEnablement);
      setEnablementSource(result.enablementSource);
      showSuccess('Agent enablement reset to default.');
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update agent enablement');
    } finally {
      setSavingEnablement(false);
    }
  };

  const handleReviewToggle = async (nextRequired: boolean) => {
    setSavingReview(true);
    setError(null);
    try {
      const result = await patchCaseAgentPolicy(caseId, { review: nextRequired ? 'required' : 'not_required' });
      setReview(result.review);
      setResolvedReview(result.resolvedReview);
      setReviewSource(result.reviewSource);
      showSuccess('Review setting updated.');
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update human review setting');
    } finally {
      setSavingReview(false);
    }
  };

  const handleResetReviewToInherit = async () => {
    setSavingReview(true);
    setError(null);
    try {
      const result = await patchCaseAgentPolicy(caseId, { review: 'inherit' });
      setReview(result.review);
      setResolvedReview(result.resolvedReview);
      setReviewSource(result.reviewSource);
      showSuccess('Review setting reset to default.');
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update human review setting');
    } finally {
      setSavingReview(false);
    }
  };

  const handlePause = async () => {
    setPausing(true);
    setPauseError(null);
    try {
      await pauseAgent(caseId);
      onPaused?.();
    } catch (err) {
      setPauseError(err instanceof Error ? err.message : 'Failed to pause the agent');
    } finally {
      setPausing(false);
    }
  };

  const enablementOn = resolvedEnablement === 'enabled';
  const reviewOn = resolvedReview === 'required';

  return (
    <section
      data-testid="case-agent-policy-section"
      aria-label="Agent policy"
      className="flex flex-col gap-2"
    >
      {loadingPolicy ? (
        <div className="flex gap-6" data-testid="case-agent-policy-loading" aria-busy="true">
          <Skeleton className="h-9 w-44" />
          <Skeleton className="h-9 w-44" />
        </div>
      ) : (
        <div
          className="flex flex-wrap items-start gap-x-6 gap-y-3"
          data-testid="case-agent-policy-form"
        >
          {/* Enablement */}
          <div className="flex items-start gap-2.5">
            <Switch
              id="case-agent-enablement"
              data-testid="case-agent-enablement-switch"
              checked={enablementOn}
              onCheckedChange={(v) => void handleEnablementToggle(v)}
              disabled={savingEnablement || loadFailed}
              aria-describedby="case-agent-enablement-caption"
              className="mt-0.5"
            />
            <div className="min-w-0">
              <label
                htmlFor="case-agent-enablement"
                className="block text-sm font-bold leading-tight text-foreground"
              >
                Agent enabled
              </label>
              <p
                id="case-agent-enablement-caption"
                data-testid="case-agent-enablement-caption"
                className="mt-0.5 text-xs leading-snug text-muted-foreground"
              >
                {enablementOn ? 'On' : 'Off'} ·{' '}
                {enablement === 'inherit'
                  ? `Inherited from ${formatCasePolicySource(enablementSource)}`
                  : (
                    <>
                      Overridden for this case
                      {' — '}
                      <button
                        type="button"
                        data-testid="case-agent-enablement-reset"
                        className="relative inline text-primary underline-offset-2 hover:underline before:absolute before:-inset-x-1 before:-inset-y-3 before:content-['']"
                        onClick={() => void handleResetEnablementToInherit()}
                        disabled={savingEnablement}
                      >
                        Use default
                      </button>
                    </>
                  )}
              </p>
            </div>
          </div>

          {/* Review gate */}
          <div className="flex items-start gap-2.5">
            <Switch
              id="case-agent-review"
              data-testid="case-agent-review-switch"
              checked={reviewOn}
              onCheckedChange={(v) => void handleReviewToggle(v)}
              disabled={savingReview || loadFailed}
              aria-describedby="case-agent-review-caption"
              className="mt-0.5"
            />
            <div className="min-w-0">
              <label
                htmlFor="case-agent-review"
                className="block text-sm font-bold leading-tight text-foreground"
              >
                Review replies before they send
              </label>
              <p
                id="case-agent-review-caption"
                data-testid="case-agent-review-caption"
                className="mt-0.5 text-xs leading-snug text-muted-foreground"
              >
                {reviewOn ? 'Yes' : 'No'} ·{' '}
                {review === 'inherit'
                  ? `Inherited from ${formatCasePolicySource(reviewSource)}`
                  : (
                    <>
                      Overridden for this case
                      {' — '}
                      <button
                        type="button"
                        data-testid="case-agent-review-reset"
                        className="relative inline text-primary underline-offset-2 hover:underline before:absolute before:-inset-x-1 before:-inset-y-3 before:content-['']"
                        onClick={() => void handleResetReviewToInherit()}
                        disabled={savingReview}
                      >
                        Use default
                      </button>
                    </>
                  )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <HelpTooltip
              text={
                'Agent enabled: the assistant handles new messages and can take routine actions on your behalf.\n' +
                'Review replies before they send: every agent action pauses for your approval before it sends.\n' +
                'These override your landlord default for this case only; each switch takes effect immediately.'
              }
              maxWidth={300}
            />

            {agentState !== 'paused' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-h-[44px] text-xs"
                onClick={() => void handlePause()}
                disabled={pausing || loadingPolicy}
                data-testid="case-agent-policy-pause-button"
                aria-label="Pause the agent on this case"
              >
                {pausing ? 'Pausing…' : 'Pause agent'}
              </Button>
            )}
          </div>
        </div>
      )}

      {pauseError && (
        <p role="alert" data-testid="case-agent-policy-pause-error" className="text-xs text-destructive">
          {pauseError}
        </p>
      )}

      {error && (
        <div
          role="alert"
          data-testid="case-agent-policy-error"
          className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
        >
          <p>{error}</p>
          {loadFailed && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              data-testid="case-agent-policy-retry"
              onClick={() => void loadPolicy()}
            >
              Retry
            </Button>
          )}
        </div>
      )}

      {successMsg && (
        <div
          role="status"
          aria-live="polite"
          data-testid="case-agent-policy-success"
          className="p-2 bg-success/10 border border-success/25 rounded-md text-success text-xs"
        >
          {successMsg}
        </div>
      )}
    </section>
  );
}

export default CaseAgentPolicySection;
